import { useEffect, useState } from 'react';
import { PopUpSummaryProps } from '@/types/game';
import { useAppDispatch, useAppSelector } from '@/store';
import { unitSelector } from '@/store/reducers/unit';
import { authActions, authSelector } from '@/store/reducers/auth';
import { authService } from '@/services';
import AppLoaderCircle from '@/components/AppLoaderCircle';
import FinishLevel from './components/FinishLevel';
import FinishUnit from './components/FinishUnit';
import LevelCompleteNotice from './components/LevelCompleteNotice';

const PopUpFinishUnit = ({ className, emitOnQuit }: PopUpSummaryProps) => {
  const dispatch = useAppDispatch();
  const { selectedUnit, units, topics } = useAppSelector(unitSelector);
  const { user, userId } = useAppSelector(authSelector);

  const isLastUnit = selectedUnit.order === units.length;

  const [isRefreshingGroupSettings, setIsRefreshingGroupSettings] = useState(isLastUnit);

  useEffect(() => {
    if (!isLastUnit) return;

    let cancelled = false;
    authService
      .me(userId)
      .then(resp => {
        const groupSettings = resp?.data?.data?.metadata?.organizationDetails?.groupSettings;
        if (!cancelled) {
          dispatch(authActions.setGroupSettings(groupSettings));
        }
      })
      .catch(() => {
        // Transient failure - fall back to the already-cached groupSettings below.
      })
      .finally(() => {
        if (!cancelled) setIsRefreshingGroupSettings(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isLastUnit]);

  if (isRefreshingGroupSettings) {
    return <AppLoaderCircle />;
  }

  const allowIndividualAdvancement =
    user?.metadata?.organizationDetails?.groupSettings?.allowIndividualAdvancement ?? true;
  const isLevelCompleteBlocked = isLastUnit && allowIndividualAdvancement === false;

  if (isLevelCompleteBlocked) {
    return <LevelCompleteNotice className={className} emitOnQuit={emitOnQuit} />;
  }

  return isLastUnit ? (
    <FinishLevel />
  ) : (
    <FinishUnit
      selectedUnit={selectedUnit}
      units={units}
      topics={topics}
      className={className}
      emitOnQuit={emitOnQuit}
    />
  );
};

export default PopUpFinishUnit;
